/**
 * Money, and the commission arithmetic both partner domains share.
 *
 * ONE RULE, and everything else follows from it: money is an integer number of
 * centavos, and it never becomes a float. `0.1 + 0.2 !== 0.3` is not a rounding
 * curiosity in a gaming ledger — it is a reconciliation break that a cashier
 * has to chase. Every amount crossing a boundary is `bigint` in TypeScript and
 * BIGINT in Postgres.
 */

/** An amount in centavos. 100n === ₱1.00 */
export type Centavos = bigint;

export const CURRENCY = 'PHP' as const;
export const CENTAVOS_PER_PESO = 100n;

/* ── Parsing ───────────────────────────────────────────────────────────── */

/**
 * Parse a human-entered peso amount into centavos.
 * Accepts "1,234.56", "₱1234.56", "1234", " 1234.5 ". Rejects anything else
 * rather than guessing — a mistyped adjustment is not a value to coerce.
 */
export function parsePesos(input: string): Centavos {
  const cleaned = input.replace(/[₱,\s]/g, '');
  if (!/^-?\d+(\.\d{1,2})?$/.test(cleaned)) {
    throw new RangeError(
      `"${input}" is not a peso amount. Expected digits with at most two decimal places, e.g. 1234.56`,
    );
  }
  const negative = cleaned.startsWith('-');
  const [whole = '0', fraction = ''] = cleaned.replace('-', '').split('.');
  const centavos = BigInt(whole) * CENTAVOS_PER_PESO + BigInt(fraction.padEnd(2, '0'));
  return negative ? -centavos : centavos;
}

/** Read a BIGINT that the pg driver handed back as a string. */
export function fromDb(value: string | number | bigint | null | undefined): Centavos {
  if (value === null || value === undefined) return 0n;
  return BigInt(value);
}

/* ── Formatting ────────────────────────────────────────────────────────── */

const grouping = new Intl.NumberFormat('en-PH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** "₱1,234.56". The canonical form for any single figure. */
export function formatPesos(amount: Centavos, { sign = false } = {}): string {
  const negative = amount < 0n;
  const abs = negative ? -amount : amount;
  const body = grouping.format(Number(abs / CENTAVOS_PER_PESO)).split('.')[0]!;
  const cents = (abs % CENTAVOS_PER_PESO).toString().padStart(2, '0');
  const prefix = negative ? '−' : sign ? '+' : '';
  return `${prefix}₱${body}.${cents}`;
}

/**
 * "₱84.2M" — the dashboard's compact form.
 * Used only where the exact figure is available elsewhere on the screen; a
 * withdrawal queue never abbreviates the amount being approved.
 */
export function formatPesosCompact(amount: Centavos): string {
  const negative = amount < 0n;
  const pesos = Number((negative ? -amount : amount) / CENTAVOS_PER_PESO);
  const prefix = negative ? '−₱' : '₱';
  if (pesos >= 1_000_000_000) return `${prefix}${(pesos / 1_000_000_000).toFixed(1)}B`;
  if (pesos >= 1_000_000) return `${prefix}${(pesos / 1_000_000).toFixed(1)}M`;
  if (pesos >= 10_000) return `${prefix}${(pesos / 1_000).toFixed(1)}K`;
  return formatPesos(amount);
}

/** Whole-number counts, grouped: "126,480". */
export function formatCount(n: number | bigint): string {
  return new Intl.NumberFormat('en-PH').format(n);
}

/* ── Percentages and rates ──────────────────────────────────────────────── */

/**
 * A commission rate held in BASIS POINTS (1/100th of a percent).
 * 5.0% is 500. Integers again, for the same reason as centavos: a 4.5% split
 * stored as 0.045 and multiplied by a large GGR does not round the way an
 * auditor expects it to.
 */
export type BasisPoints = number;

export const percentToBasisPoints = (pct: number): BasisPoints => Math.round(pct * 100);
export const basisPointsToPercent = (bp: BasisPoints): number => bp / 100;
export const formatRate = (bp: BasisPoints): string => `${basisPointsToPercent(bp).toFixed(1)}%`;

/**
 * Apply a rate to an amount, rounding half-up on the absolute value so that a
 * credit and its mirror-image debit round to the same magnitude. Banker's
 * rounding would make a reversal fail to cancel its original by one centavo.
 */
export function applyRate(amount: Centavos, rate: BasisPoints): Centavos {
  const negative = amount < 0n;
  const abs = negative ? -amount : amount;
  const scaled = abs * BigInt(rate);
  const rounded = (scaled + 5000n) / 10000n;
  return negative ? -rounded : rounded;
}

/* ── The figures both partner domains compute ───────────────────────────── */

export interface GgrInput {
  /** Everything staked in the period. */
  totalBets: Centavos;
  /** Everything paid back out as wins. */
  totalWins: Centavos;
  /** Bonus money the operator funded. */
  bonusCost?: Centavos;
  /** What the game providers charged. */
  providerFees?: Centavos;
}

/**
 * Gross gaming revenue.
 *
 * The design states the definition it means: "GGR = total bets − total wins,
 * excluding voided rounds and bonus-funded wins". Void exclusion happens at the
 * query that produces the inputs; this is the arithmetic.
 */
export const grossGamingRevenue = (i: GgrInput): Centavos => i.totalBets - i.totalWins;

/** Net gaming revenue — what a commission is actually calculated on. */
export const netGamingRevenue = (i: GgrInput): Centavos =>
  grossGamingRevenue(i) - (i.bonusCost ?? 0n) - (i.providerFees ?? 0n);

/**
 * Carry a negative period forward instead of paying a negative commission.
 *
 * Carry-over is ALWAYS zero or negative: a partner never owes the platform out
 * of pocket, but a losing period must offset the next winning one or a partner
 * can bank the good weeks and walk away from the bad ones.
 */
export interface CommissionPeriod {
  netRevenue: Centavos;
  rate: BasisPoints;
  /** Zero or negative. */
  carryIn: Centavos;
}

export interface CommissionResult {
  /** The base after carry-over is applied. Never negative. */
  commissionable: Centavos;
  /** What is payable this period. Never negative. */
  commission: Centavos;
  /** Zero or negative, carried into the next period. */
  carryOut: Centavos;
}

export function computeCommission({
  netRevenue,
  rate,
  carryIn,
}: CommissionPeriod): CommissionResult {
  if (carryIn > 0n) {
    throw new RangeError(
      `carryIn must be zero or negative (got ${carryIn}). A positive carry-over would pay a partner twice for the same revenue.`,
    );
  }
  const offset = netRevenue + carryIn;
  if (offset <= 0n) {
    return { commissionable: 0n, commission: 0n, carryOut: offset };
  }
  return { commissionable: offset, commission: applyRate(offset, rate), carryOut: 0n };
}

/**
 * The agent override: what a recruiting agent earns on a sub-agent's player.
 *
 * The design states it as a DIFFERENTIAL — "Your cut = net GGR × (your rate −
 * their split)" — and caps the sub-agent's split at the recruiter's own rate.
 * A negative differential would mean the recruiter pays to have a downline, so
 * the cap is enforced here rather than assumed upstream.
 */
export function computeOverride(
  netRevenue: Centavos,
  ownRate: BasisPoints,
  subAgentSplit: BasisPoints,
): Centavos {
  if (subAgentSplit > ownRate) {
    throw new RangeError(
      `A sub-agent split (${formatRate(subAgentSplit)}) cannot exceed the recruiting agent's own rate (${formatRate(ownRate)}).`,
    );
  }
  return applyRate(netRevenue, ownRate - subAgentSplit);
}

/* ── Banded commission ──────────────────────────────────────────────────────
   A tiered plan — "4% under ₱10M, 7% above" — cannot be written as a scalar
   rate, so it is written as a set of bands over the commissionable amount.

   These live here, with the rest of the arithmetic both partner domains share,
   for the same reason `computeCommission` does: two engines that each carry
   their own copy of a rounding or a threshold rule are two engines that will
   eventually disagree about a payable, and the disagreement will be discovered
   by a partner rather than by a test. Agent plans are the only caller today;
   nothing here knows or cares which domain asked.

   WHOLE-AMOUNT, NOT MARGINAL. Crossing a threshold re-prices the WHOLE period,
   it does not price only the excess. On an ₱11M period the two readings differ
   by ₱300,000 and no screen would show which was meant, so it is stated here,
   in the schema comment on `commission_plan_band`, and on the plan dialog.  */

/** One band of a plan: a rate that applies from `minNetRevenue` upwards. */
export interface CommissionBand {
  /** Centavos. The lowest band is 0 — every plan prices revenue from zero. */
  minNetRevenue: Centavos;
  rateBp: BasisPoints;
}

/**
 * Put a band set in the order the arithmetic needs, and refuse one that cannot
 * answer the question.
 *
 * Refuses rather than repairing: a plan with no entry band has a range of
 * revenue no rate applies to, and silently treating that as 0% would pay a
 * partner nothing for a period they earned on.
 */
export function normaliseBands(bands: readonly CommissionBand[]): CommissionBand[] {
  if (bands.length === 0) {
    throw new RangeError('A commission plan needs at least one band. There is no rate otherwise.');
  }
  const sorted = [...bands].toSorted((a, b) =>
    a.minNetRevenue < b.minNetRevenue ? -1 : a.minNetRevenue > b.minNetRevenue ? 1 : 0,
  );
  if (sorted[0]!.minNetRevenue !== 0n) {
    throw new RangeError(
      `The lowest band starts at ${sorted[0]!.minNetRevenue} centavos, so revenue below that has no rate. Every plan must price revenue from zero.`,
    );
  }
  for (let i = 1; i < sorted.length; i++) {
    const previous = sorted[i - 1]!;
    const current = sorted[i]!;
    if (current.minNetRevenue === previous.minNetRevenue) {
      throw new RangeError(
        `Two bands both start at ${current.minNetRevenue} centavos. A threshold can carry one rate.`,
      );
    }
    if (current.rateBp <= previous.rateBp) {
      throw new RangeError(
        `A band at ${current.minNetRevenue} centavos pays ${formatRate(current.rateBp)}, no more than the ${formatRate(previous.rateBp)} beneath it. Bands are whole-amount, so that would pay more for earning less.`,
      );
    }
  }
  return sorted;
}

/**
 * The rate a given amount earns under a band set.
 *
 * The highest band whose threshold the amount has reached. A negative or zero
 * amount earns the entry rate — there is nothing to pay either way, and
 * returning the entry rate keeps the printed rate stable across a losing period
 * rather than showing an agent a rate they have never been on.
 */
export function resolveBandRate(bands: readonly CommissionBand[], amount: Centavos): BasisPoints {
  const sorted = normaliseBands(bands);
  let rate = sorted[0]!.rateBp;
  for (const band of sorted) {
    if (amount >= band.minNetRevenue) rate = band.rateBp;
    else break;
  }
  return rate;
}

export interface BandedCommissionInput extends CommissionPeriod {
  bands: readonly CommissionBand[];
  /** A fixed amount for the period, in centavos. Zero on a plan with none. */
  retainer?: Centavos;
}

export interface BandedCommissionResult extends CommissionResult {
  /** The band the period actually landed in. */
  rateBp: BasisPoints;
  /** The share earned on revenue, before the retainer is added. */
  revenueShare: Centavos;
  /** What the retainer contributed. */
  retainer: Centavos;
}

/**
 * A period's commission under a banded plan, with an optional retainer.
 *
 * The carry-over rule is `computeCommission`'s, unchanged and reused rather
 * than restated: a negative period carries forward, never bills the partner,
 * and the band is chosen on the amount AFTER the carry-in is applied — the
 * threshold is a threshold on what is actually being paid on, not on a gross
 * figure a previous loss has already eaten.
 *
 * The RETAINER is added after, and is not subject to the carry-over: it is a
 * fee for being on the plan, not a share of revenue, so a losing period still
 * pays it. It does not turn the carry-out positive either — the carry-out is
 * the revenue position and stays exactly what `computeCommission` made it.
 */
export function computeBandedCommission({
  netRevenue,
  carryIn,
  bands,
  retainer = 0n,
}: Omit<BandedCommissionInput, 'rate'>): BandedCommissionResult {
  if (retainer < 0n) {
    throw new RangeError(`A retainer must be zero or positive (got ${retainer}).`);
  }
  const sorted = normaliseBands(bands);
  // Applied once to find the base, so the band is chosen on the same figure
  // the rate is then applied to.
  const base = computeCommission({ netRevenue, rate: sorted[0]!.rateBp, carryIn });
  const rateBp = resolveBandRate(sorted, base.commissionable);
  const revenueShare = applyRate(base.commissionable, rateBp);
  return {
    commissionable: base.commissionable,
    commission: revenueShare + retainer,
    carryOut: base.carryOut,
    rateBp,
    revenueShare,
    retainer,
  };
}

/* ── Carry-over, AGED ───────────────────────────────────────────────────────
   `computeCommission` above carries a loss forward. It does not know how long
   for, and it cannot: "negative carry-over runs for three months" is a term of
   the plan, and a scalar `carryOut` has no room to say WHEN the loss it holds
   was booked. Two losses eight weeks apart look identical inside one number,
   so one number cannot decide which of them has run out.

   The ages come back by REPLAYING the period chain. A losing period opens a
   LOT — an amount, and the date it stops being allowed to offset. A winning
   period consumes lots OLDEST FIRST, because a lot that is about to expire is
   worth more used than kept. A lot still standing when its date passes is
   written off, and the write-off is a figure a partner is entitled to see
   rather than an amount that silently stops reducing their commission.

   OLDEST FIRST is the only ordering that does not quietly favour one side:
   newest-first would let a partner's own good week expire their oldest loss and
   then still be offset by it next month, and pro-rata would expire a fraction
   of every lot on a date none of them fall on.

   This lives here, with the rest of the arithmetic both domains share, for the
   reason the file's other exports do — agents take the term from their
   commission plan and affiliates from `platform.setting`, and two engines each
   carrying their own copy of "which loss expires first" would eventually
   disagree in a partner's favour on one side and against them on the other. ── */

/** An un-recovered loss, and the moment it stops being able to offset. */
export interface CarryLot {
  /** POSITIVE centavos. A lot is the magnitude of a loss, not the loss itself. */
  amount: Centavos;
  /**
   * Epoch milliseconds. `Number.POSITIVE_INFINITY` for a plan with no term —
   * `carry_over_months = 0` means carried forever, not written off at once.
   */
  expiresAt: number;
}

/** One settled period, as the carry replay sees it. */
export interface CarryPeriod {
  /** Epoch milliseconds — when the period opened. Lots expiring by then are gone. */
  startsAt: number;
  /** Net revenue for the period. Negative opens a lot; positive consumes them. */
  netRevenue: Centavos;
  /** Epoch milliseconds at which a loss booked in THIS period would expire. */
  expiresAt: number;
}

export interface CarryState {
  /** Still live, in expiry order. */
  lots: CarryLot[];
  /**
   * The position BEFORE the write-off — everything the partner was still
   * carrying when the period opened. Zero or negative.
   *
   * This is the figure the statement's `carry_in` holds, because a write-off
   * that was netted off before it was recorded would be invisible: the carry
   * would simply be smaller than last month's carry-out and nothing would say
   * that a loss had run out of road.
   */
  carryBroughtForward: Centavos;
  /** What is left to offset with, after the write-off. Zero or negative. */
  carryIn: Centavos;
  /** Written off at `asOf`. Zero or positive. */
  writtenOff: Centavos;
}

const totalOf = (lots: readonly CarryLot[]): Centavos =>
  lots.reduce((sum, lot) => sum + lot.amount, 0n);

/** Drop every lot whose term has run out at `asOf`, and say how much that was. */
export function expireCarryLots(
  lots: readonly CarryLot[],
  asOf: number,
): { lots: CarryLot[]; writtenOff: Centavos } {
  const live: CarryLot[] = [];
  let writtenOff = 0n;
  for (const lot of lots) {
    if (lot.expiresAt <= asOf) writtenOff += lot.amount;
    else live.push(lot);
  }
  return { lots: live, writtenOff };
}

/**
 * Spend `amount` of positive revenue against the standing lots, oldest first.
 *
 * Returns what was actually absorbed, which is the smaller of the revenue and
 * the outstanding carry. The caller does not need it — `computeCommission`
 * derives the same answer from the totals — but a partial lot has to be written
 * back at its reduced amount or the next period would offset against a loss
 * that has already been recovered.
 */
export function consumeCarryLots(
  lots: readonly CarryLot[],
  amount: Centavos,
): { lots: CarryLot[]; consumed: Centavos } {
  if (amount <= 0n) return { lots: [...lots], consumed: 0n };
  let left = amount;
  const remaining: CarryLot[] = [];
  for (const lot of lots) {
    if (left <= 0n) {
      remaining.push(lot);
      continue;
    }
    if (lot.amount <= left) {
      left -= lot.amount;
      continue;
    }
    remaining.push({ amount: lot.amount - left, expiresAt: lot.expiresAt });
    left = 0n;
  }
  return { lots: remaining, consumed: amount - left };
}

/**
 * Replay a partner's settled periods and say what they are carrying into the
 * next one.
 *
 * `periods` must be in chronological order and must be the partner's OWN
 * settled results — the figures that were published, not figures recomputed
 * now. A settled period is history; this reads it, it never restates it.
 *
 * `asOf` is the instant the NEXT period opens, so a lot that ran out between
 * the last period and this one is written off here rather than surviving a week
 * too long.
 */
export function replayCarryOver(periods: readonly CarryPeriod[], asOf: number): CarryState {
  let lots: CarryLot[] = [];
  for (const period of periods) {
    // Expire BEFORE the period's own revenue is applied: a loss whose term ran
    // out cannot be recovered by the very period that outlived it.
    lots = expireCarryLots(lots, period.startsAt).lots;
    if (period.netRevenue < 0n) {
      lots.push({ amount: -period.netRevenue, expiresAt: period.expiresAt });
      // Kept in EXPIRY order, not in the order the losses were booked. The two
      // are the same list until a plan's carry-over term changes, and then they
      // are not: a loss booked later under a one-month term runs out before an
      // older one carried for three, and consuming the older one first would
      // let the newer expire while it was still recoverable.
      // `toSorted` is stable, so equal terms keep their chronological order.
      lots = lots.toSorted((a, b) => a.expiresAt - b.expiresAt);
    } else if (period.netRevenue > 0n) {
      lots = consumeCarryLots(lots, period.netRevenue).lots;
    }
  }
  const broughtForward = -totalOf(lots);
  const expired = expireCarryLots(lots, asOf);
  return {
    lots: expired.lots,
    carryBroughtForward: broughtForward,
    carryIn: -totalOf(expired.lots),
    writtenOff: expired.writtenOff,
  };
}
